import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserApi } from '../../services/api/UserApi';
import { DATA_SERVICE_BASE_URL } from '../../services/ApiService';
import { fetchWithAuth } from '../../services/AuthService';

/**
 * AdminGrantProAccess
 * Admin-only page to grant or revoke complimentary pro access to users without requiring payment
 */
function AdminGrantProAccess() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [reason, setReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  const isAdmin = !!user?.roles?.some(r => r.roleName === 'admin');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      // Search by email or user ID
      let response;
      const trimmed = searchQuery.trim();
      
      // If query looks like a number, try ID search
      if (/^\d+$/.test(trimmed)) {
        response = await UserApi.getUser(trimmed);
        if (response.ok) {
          const userData = await response.json();
          setSearchResults([userData]);
        } else {
          throw new Error('User not found');
        }
      } else {
        // Try email search
        response = await UserApi.getUserByEmail(trimmed);
        if (response.ok) {
          const userData = await response.json();
          setSearchResults([userData]);
        } else {
          // Fallback to list with search parameter
          const listResponse = await fetchWithAuth(
            `${DATA_SERVICE_BASE_URL}/api/users?search=${encodeURIComponent(trimmed)}&size=10`
          );
          if (listResponse.ok) {
            const data = await listResponse.json();
            setSearchResults(data.users || []);
          } else {
            throw new Error('Search failed');
          }
        }
      }
    } catch (error) {
      setSearchError(error.message || 'Failed to search for user');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = async (userResult) => {
    setSelectedUser(userResult);
    setActionMessage(null);
    
    // Try to fetch current subscription status
    try {
      const subResponse = await fetchWithAuth(
        `${DATA_SERVICE_BASE_URL}/api/users/${userResult.userId || userResult.user_id}/subscription`
      );
      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSelectedUser({ ...userResult, subscription: subData });
      }
    } catch (error) {
      // Ignore - user may not have subscription
    }
  };

  const handleGrantAccess = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    setActionMessage(null);

    try {
      const userId = selectedUser.userId || selectedUser.user_id;
      const response = await fetchWithAuth(
        `${DATA_SERVICE_BASE_URL}/api/users/${userId}/grant-pro-access`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason.trim() || undefined }),
        }
      );

      if (response.ok) {
        await response.json();
        setActionMessage({
          type: 'success',
          text: `Successfully granted complimentary pro access to ${selectedUser.email}`
        });
        // Refresh selected user's subscription data
        await handleSelectUser(selectedUser);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to grant access');
      }
    } catch (error) {
      setActionMessage({
        type: 'error',
        text: error.message || 'Failed to grant access'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAccess = async () => {
    if (!selectedUser) return;
    
    if (!window.confirm(`Are you sure you want to revoke pro access from ${selectedUser.email}?`)) {
      return;
    }

    setActionLoading(true);
    setActionMessage(null);

    try {
      const userId = selectedUser.userId || selectedUser.user_id;
      const response = await fetchWithAuth(
        `${DATA_SERVICE_BASE_URL}/api/users/${userId}/revoke-pro-access`,
        {
          method: 'POST',
        }
      );

      if (response.ok) {
        setActionMessage({
          type: 'success',
          text: `Successfully revoked complimentary pro access from ${selectedUser.email}`
        });
        // Refresh selected user's subscription data
        await handleSelectUser(selectedUser);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to revoke access');
      }
    } catch (error) {
      setActionMessage({
        type: 'error',
        text: error.message || 'Failed to revoke access'
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Grant Pro Access</h2>
        <div>Admin access required.</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h2>Grant Complimentary Pro Access</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
        Grant or revoke pro access to users without requiring Stripe payment. 
        This is useful for test accounts, internal users, or promotional access.
      </p>

      {/* Search Section */}
      <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0 }}>Find User</h3>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter email or user ID..."
            style={{ flex: 1 }}
            disabled={searching}
          />
          <button type="submit" className="btn-primary" disabled={searching || !searchQuery.trim()}>
            {searching ? 'Searching...' : 'Search'}
          </button>
        </form>

        {searchError && (
          <div style={{ padding: '0.75rem', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', color: '#c00' }}>
            {searchError}
          </div>
        )}

        {searchResults.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.5rem' }}>Search Results ({searchResults.length})</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {searchResults.map((userResult) => {
                const userId = userResult.userId || userResult.user_id;
                const firstName = userResult.firstName || userResult.first_name;
                const lastName = userResult.lastName || userResult.last_name;
                
                return (
                  <div
                    key={userId}
                    onClick={() => handleSelectUser(userResult)}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid var(--border-light)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: selectedUser && (selectedUser.userId === userId || selectedUser.user_id === userId) 
                        ? 'var(--primary-light)' 
                        : '#fff',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{firstName} {lastName}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{userResult.email}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      User ID: {userId}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Selected User Section */}
      {selectedUser && (
        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)' }}>
          <h3 style={{ marginTop: 0 }}>
            {selectedUser.firstName || selectedUser.first_name} {selectedUser.lastName || selectedUser.last_name}
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <div style={{ color: 'var(--text-muted)' }}>Email:</div>
            <div>{selectedUser.email}</div>
            
            <div style={{ color: 'var(--text-muted)' }}>User ID:</div>
            <div>{selectedUser.userId || selectedUser.user_id}</div>
            
            <div style={{ color: 'var(--text-muted)' }}>Current Status:</div>
            <div>
              {selectedUser.subscription ? (
                <span style={{ 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px',
                  background: selectedUser.subscription.status === 'COMPLIMENTARY' 
                    ? '#e7f5ff' 
                    : selectedUser.subscription.status === 'ACTIVE' || selectedUser.subscription.status === 'TRIALING'
                    ? '#e7ffe7'
                    : '#f5f5f5',
                  color: selectedUser.subscription.status === 'COMPLIMENTARY'
                    ? '#0066cc'
                    : selectedUser.subscription.status === 'ACTIVE' || selectedUser.subscription.status === 'TRIALING'
                    ? '#008800'
                    : '#666',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}>
                  {selectedUser.subscription.status}
                  {selectedUser.subscription.provider === 'admin' && ' (Admin Granted)'}
                </span>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>No subscription (Free tier)</span>
              )}
            </div>
          </div>

          {selectedUser.subscription?.provider === 'admin' && selectedUser.subscription?.status === 'COMPLIMENTARY' && (
            <div style={{ padding: '0.75rem', background: '#e7f5ff', border: '1px solid #b3d9ff', borderRadius: '8px', marginBottom: '1rem' }}>
              <strong>Active Complimentary Access</strong>
              {selectedUser.subscription.metadata?.grantedAt && (
                <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  Granted: {new Date(selectedUser.subscription.metadata.grantedAt).toLocaleString()}
                </div>
              )}
              {selectedUser.subscription.metadata?.reason && (
                <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  Reason: {selectedUser.subscription.metadata.reason}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Test account, Internal user, Promotional access..."
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
              disabled={actionLoading}
            />
          </div>

          {actionMessage && (
            <div style={{
              padding: '0.75rem',
              background: actionMessage.type === 'success' ? '#e7ffe7' : '#fee',
              border: `1px solid ${actionMessage.type === 'success' ? '#88dd88' : '#fcc'}`,
              borderRadius: '8px',
              color: actionMessage.type === 'success' ? '#006600' : '#c00',
              marginBottom: '1rem'
            }}>
              {actionMessage.text}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {selectedUser.subscription?.provider === 'admin' && selectedUser.subscription?.status === 'COMPLIMENTARY' ? (
              <button
                onClick={handleRevokeAccess}
                className="btn-danger"
                disabled={actionLoading}
              >
                {actionLoading ? 'Revoking...' : 'Revoke Pro Access'}
              </button>
            ) : (
              <button
                onClick={handleGrantAccess}
                className="btn-primary"
                disabled={actionLoading}
              >
                {actionLoading ? 'Granting...' : 'Grant Pro Access'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="info-panel" style={{ marginTop: '1.5rem' }}>
        <h4>About Complimentary Pro Access</h4>
        <p>
          Complimentary pro access grants users full pro-tier features without requiring Stripe payment. 
          This creates an admin-managed subscription that bypasses the payment system.
        </p>
        <p style={{ marginTop: '0.5rem' }}>
          <strong>Use cases:</strong> Test accounts, internal employees, promotional access, partner accounts, or special circumstances.
        </p>
        <p style={{ marginTop: '0.5rem', color: 'var(--intent-warning-text)' }}>
          <strong>Note:</strong> Complimentary subscriptions do not expire automatically. 
          You must manually revoke access when needed.
        </p>
      </div>
    </div>
  );
}

export default AdminGrantProAccess;
